import React from "react";
import { MdPersonOutline, MdWarning } from "react-icons/md";
import { Ticket } from "../../../../interfaces/Ticket";
import { TicketPriorityBadge, TicketStateBadge } from "./TicketBadges";

interface Props {
  tickets: Ticket[];
  loading: boolean;
  onOpen: (ticket: Ticket) => void;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Lista de chamados. Mesma tela atende fila e "meus chamados" — quem filtra decide o que aparece. */
const TicketTable: React.FC<Props> = ({ tickets, loading, onOpen }) => {
  if (loading) {
    return (
      <div className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="py-14 text-center text-sm text-gray-500 dark:text-gray-400">
        Nenhum chamado encontrado com os filtros atuais.
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[1100px] border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-white/10 dark:text-gray-400">
            <th className="whitespace-nowrap px-3 py-2.5">Número</th>
            <th className="px-3 py-2.5">Assunto</th>
            <th className="whitespace-nowrap px-3 py-2.5">Cliente</th>
            <th className="whitespace-nowrap px-3 py-2.5">Categoria</th>
            <th className="whitespace-nowrap px-3 py-2.5">Fila</th>
            <th className="whitespace-nowrap px-3 py-2.5">Estado</th>
            <th className="whitespace-nowrap px-3 py-2.5">Prioridade</th>
            <th className="whitespace-nowrap px-3 py-2.5">Responsável</th>
            <th className="whitespace-nowrap px-3 py-2.5">Aberto em</th>
            <th className="whitespace-nowrap px-3 py-2.5">SLA</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr
              key={t.id}
              onClick={() => onOpen(t)}
              className={`cursor-pointer border-b border-gray-50 transition-colors hover:bg-gray-50 dark:border-white/5 dark:hover:bg-white/5 ${
                t.slaResolutionBreached ? "bg-red-50/60 dark:bg-red-900/10" : ""
              }`}
            >
              <td className="whitespace-nowrap px-3 py-3 font-mono text-xs font-bold text-navy-700 dark:text-white">
                #{t.number}
              </td>
              <td className="max-w-[320px] truncate px-3 py-3 font-medium text-navy-700 dark:text-white">
                {t.subject}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-gray-700 dark:text-gray-300">
                {t.clientName ?? `Cliente #${t.clientId}`}
              </td>
              <td className="max-w-[220px] truncate px-3 py-3 text-gray-700 dark:text-gray-300">
                {t.categoryPath ?? (
                  <span className="italic text-gray-400 dark:text-gray-500">Sem categoria</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-gray-700 dark:text-gray-300">
                {t.queueName ?? <span className="italic text-gray-400 dark:text-gray-500">Sem fila</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-3">
                <TicketStateBadge state={t.state} />
              </td>
              <td className="whitespace-nowrap px-3 py-3">
                <TicketPriorityBadge priority={t.priority} />
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-gray-700 dark:text-gray-300">
                {t.assigneeName ? (
                  <span className="inline-flex items-center gap-1">
                    <MdPersonOutline className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    {t.assigneeName}
                  </span>
                ) : (
                  <span className="italic text-gray-400 dark:text-gray-500">Ninguém</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-gray-500 dark:text-gray-400">
                {fmtDate(t.openedAt)}
              </td>
              <td className="whitespace-nowrap px-3 py-3">
                {t.slaResolutionBreached && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-[11px] font-bold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                    <MdWarning className="h-3.5 w-3.5" aria-hidden="true" />
                    Estourado
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TicketTable;
